/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
// @ts-ignore: side-effect CSS import without type declarations
import 'devextreme/dist/css/dx.light.css';
import CustomStore from 'devextreme/data/custom_store';
import { Box } from '@mui/material';
import { FilterOption, FiltersSection } from '../../../components/filters-section';
import { Scenarios } from '../../../constants/scenarios';
import {
  getActiveGroup,
  getCompanies,
  getConfiguration,
  getPeriods,
  getSchemes,
  listGroups,
} from '../../../store/slices/account-entries';
import { RootState } from '../../../store';
import { useSelector } from 'react-redux';
import DateSelectorButton from '../../../components/DateSelectorButton';
import { AvailableCombination, dataGridData, fetchAvailableCombinations } from '../../../api/accountApi';
import { getUserPreferences, saveUserPreferences } from '../../../api/apiPreferences';
import { InvoiceModal } from './InvoiceModal';
import { DocumentRegistrationModal } from './DocumentRegistration';
import { AccountingEntriesDataGrid } from '../../../components/AccountingEntriesDataGrid';

type LoadOptions = any;

const buildFixedFilter = (filterValues: Record<string, string>): any[] | null => {
  const conditions: any[] = [];
  conditions.push([
    'CodPeriodoContable', 
    filterValues.accumulated === '1' ? '<=' : '=', 
    filterValues.period
  ]);

  if (filterValues.accountingEntry === '0') {
    conditions.push('and');
    conditions.push(['Refleja', '=', 0]);
  }

  if (['0', '1'].includes(filterValues.entry)) {
    conditions.push('and');
    conditions.push(['Descuadrado', '=', filterValues.entry]);
  }

  if (conditions.length === 1) return conditions[0];
  return conditions;
};

const INITIAL_FILTER_VALUES = {
  folder: '1',
  company: '',
  scheme: '',
  period: '',
  accumulated: '0',
  accountingEntry: '1',
  entry: '-1',
};

export const AccountingEntriesGrid: React.FC = () => {
  const { companyselected, connectionId } = useSelector((state: RootState) => state.auth);
  const scenario = Scenarios.LibroDiarioEscenario;
  const { companies, scheme, periods } = useSelector((state: RootState) => state.accountEntries);

  const [filterValues, setFilterValues] = useState<Record<string, string>>(INITIAL_FILTER_VALUES);
  const [dataSourceInstance, setDataSourceInstance] = useState<CustomStore | null>(null);

  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [documentRegistrationModalOpen, setDocumentRegistrationModalOpen] = useState(false);
  const [selectedRowForModal, setSelectedRowForModal] = useState<any>(null);

  const [availableCombinations, setAvailableCombinations] = useState<AvailableCombination[]>([]);
  const [validCombinationMap, setValidCombinationMap] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchAvailableCombinations().then(data => {
      setAvailableCombinations(data);
      const map = new Set<string>();
      data.forEach(c => map.add(`${c.CompanyCode}|${c.SchemeCode}|${c.Year}`));
      setValidCombinationMap(map);
    });
  }, []);

  const isCombinationValid = useMemo(() => {
    if (!filterValues.company || !filterValues.scheme || !filterValues.period) return false;
    const year = filterValues.period.substring(0, 4);
    return validCombinationMap.has(`${filterValues.company}|${filterValues.scheme}|${year}`);
  }, [filterValues, validCombinationMap]);

  const isSearchDisabled = !isCombinationValid;

  useEffect(() => {
    if (!companyselected?.Token || !connectionId || !filterValues.company) return;
    getSchemes(filterValues.company);
    getPeriods(filterValues.company);
  }, [companyselected?.Token, connectionId, filterValues.company]);

  useEffect(() => {
    if (!companyselected?.Token || !connectionId) return;
    getConfiguration(scenario);
    getUserPreferences().then(preferences => {
      let accountingViewConfig = preferences?.data?.accountingViewConfig;
      if (!accountingViewConfig) {
        accountingViewConfig = JSON.parse(localStorage.getItem('userPreferences') || '{}')?.accountingViewConfig || {};
      } 
      setFilterValues({ ...filterValues, ...accountingViewConfig});
    }).finally(() => {
      getCompanies(filterValues.folder);
      getActiveGroup(scenario);
      listGroups(scenario);
    });
  }, [companyselected?.Token, connectionId]);

  const handleSearch = useCallback(() => {
    if (isSearchDisabled) return;
    const periodCode = filterValues.period;
    const year = periodCode.substring(0, 4);
    const baseFilters = {
      codEmpresa: filterValues.company,
      codEsquema: filterValues.scheme,
      codAno: year,
    };
    const fixedFilters = buildFixedFilter(filterValues);
    
    const newStore = new CustomStore({
      key: 'OID',
      load: async (loadOptions: LoadOptions) => {
        let finalFilter = loadOptions.filter;
        if (loadOptions?.filter?.length) {
          finalFilter = [fixedFilters, 'and', loadOptions.filter];
        } else {
          finalFilter = fixedFilters;
        }
        
        const params: Record<string, any> = {
          ...loadOptions,
          codEmpresa: baseFilters.codEmpresa,
          codEsquema: baseFilters.codEsquema,
          codAno: baseFilters.codAno,
          userData: JSON.stringify(loadOptions.userData),
          sort: JSON.stringify(loadOptions.sort),
          filter: JSON.stringify(finalFilter),
          group: JSON.stringify(loadOptions.group),
          totalSummary: JSON.stringify(loadOptions.totalSummary),
          groupSummary: JSON.stringify(loadOptions.groupSummary),
        };
        return dataGridData(params);
      }
    });
    setDataSourceInstance(newStore);
  }, [filterValues, isSearchDisabled]);

  const handleRowContextMenu = useCallback((e: any) => {
    if (e.row) {
      const rowData = e.row.data;
      if (!rowData?.TipoDocumento) return;

      const menuItems = [
        {
          text: "Ver documento",
          onItemClick: () => {
            setSelectedRowForModal(rowData);
            setInvoiceModalOpen(true);
          },
          disabled: false
        },
        // {
        //   text: "Registro documento",
        //   onItemClick: () => {
        //     setSelectedRowForModal(rowData);
        //     setDocumentRegistrationModalOpen(true);
        //   },
        //   disabled: false
        // }
      ];
      e.items = menuItems;
    }
  }, []);

  const handleFilterChange = (filterId: string, value: string) => {
    let newFilterValues = { [filterId]: value };
    if (filterId === 'folder') {
      getCompanies(value);
      newFilterValues.company = '';
      newFilterValues.scheme = '';
      newFilterValues.period = '';
    }
    if (filterId === 'company') {
      newFilterValues.scheme = '';
      newFilterValues.period = '';
    }
    setFilterValues(prev => ({ ...prev, ...newFilterValues }));
    saveUserPreferences({ accountingViewConfig: { ...filterValues, ...newFilterValues } });
    localStorage.setItem('userPreferences', JSON.stringify({ accountingViewConfig: { ...filterValues, ...newFilterValues } }));
  };

  const enrichedFilters = useMemo((): FilterOption[] => {
    const empresaOpts = companies.map(company => {
      const hasAnyCombination = availableCombinations.some(c => c.CompanyCode === company.CodEmpresa);
      return {
        label: company.Descripcion,
        value: company.CodEmpresa,
        disabled: !hasAnyCombination,
      };
    });

    let esquemaOpts: { label: string; value: string; disabled?: boolean }[] = [];
    if (filterValues.company) {
      esquemaOpts = scheme.map(s => {
        const hasCombination = availableCombinations.some(
          c => c.CompanyCode === filterValues.company && c.SchemeCode === s.CodEsquemaContable
        );
        return {
          label: `${s.CodEsquemaContable} - ${s.Descripcion}`,
          value: s.CodEsquemaContable,
          disabled: !hasCombination,
        };
      });
    } else {
      esquemaOpts = scheme.map(s => ({
        label: `${s.CodEsquemaContable} - ${s.Descripcion}`,
        value: s.CodEsquemaContable,
        disabled: true,
      }));
    }

    const allYears = Array.from(new Set(periods.map(p => p.CodPeriodoContable.substring(0, 4))));
    let anioOpts: { label: string; value: string; disabled?: boolean }[] = [];
    if (filterValues.company && filterValues.scheme) {
      anioOpts = allYears.map(ano => {
        const hasCombination = availableCombinations.some(
          c => c.CompanyCode === filterValues.company && c.SchemeCode === filterValues.scheme && c.Year === ano
        );
        return { label: ano, value: ano, disabled: !hasCombination };
      });
    } else {
      anioOpts = allYears.map(ano => ({ label: ano, value: ano, disabled: true }));
    }

    const periodDates = periods.map(p => {
      const ano = p.CodPeriodoContable.substring(0, 4);
      const isDisabled = anioOpts.find(opt => opt.value === ano)?.disabled ?? true;
      return {
        DesPeriodo: `${p.CodPeriodoContable} - ${p.Descripcion}`,
        CodPeriodo: p.CodPeriodoContable,
        Anio: ano,
        disabled: isDisabled,
      };
    });

    return [
      {
        width: 150,
        type: 'select',
        label: 'Carpeta',
        value: 'folder',
        options: [
          { label: 'Escritorio', value: '1' },
          { label: 'Bandeja', value: '3' },
          { label: 'Archivo Central', value: '8' },
        ],
      },
      {
        width: 250,
        type: 'search',
        label: 'Empresa',
        value: 'company',
        options: empresaOpts,
      },
      {
        width: 250,
        type: 'search',
        label: 'Esquema',
        value: 'scheme',
        options: esquemaOpts,
      },
      {
        label: 'Periodo',
        value: 'period',
        type: 'custom' as const,
        component: (
          <DateSelectorButton
            dates={periodDates}
            selectedDate={filterValues.period}
            onDateChange={(codigo) => {
              handleFilterChange('period', codigo);
            }}
            groupBy={'Anio'}
            buttonColor="secondary"
            modalTitle="Seleccionar período"
            placeholder="Elige una fecha"
            accumulated={filterValues.accumulated === '1'}
            onAccumulatedChange={(value) => {
              handleFilterChange('accumulated', value ? '1' : '0');
            }}
          />
        ),
      },
      {
        width: 150,
        type: 'select' as const,
        label: 'Asiento Contable',
        value: 'accountingEntry',
        options: [
          { label: 'Detallado', value: '1' },
          { label: 'Sin Reflejas', value: '0' },
        ],
      },
      {
        width: 150,
        type: 'select' as const,
        label: 'Asiento',
        value: 'entry',
        options: [
          { label: 'Todos', value: '-1' },
          { label: 'Descuadrados', value: '0' },
          { label: 'Cuadrados', value: '1' },
        ],
      },
    ];
  }, [
    companies, scheme, periods, availableCombinations, filterValues
  ]);


  return (
    <div style={{ height: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box sx={{ flexShrink: 0 }}>
        <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', backgroundColor: 'rgba(0, 0, 0, 0.02)', borderRadius: 1 }}>
          <FiltersSection
            filters={enrichedFilters as any}
            onFilterChange={handleFilterChange}
            values={filterValues as any}
            onSearch={handleSearch}
            searchButtonDisabled={isSearchDisabled}
          />
        </Box>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <AccountingEntriesDataGrid
          scenario={scenario}
          data={dataSourceInstance}
          onClickSearch={handleSearch}
          isConfigAvailable={true}
          isGroupingAvailable={true}
          onRowContextMenuPreparing={handleRowContextMenu}
        />
      </Box>

      {invoiceModalOpen && !!selectedRowForModal && <InvoiceModal 
        open={invoiceModalOpen} 
        onClose={() => {
          setSelectedRowForModal(null)
          setInvoiceModalOpen(false)
        }} 
        selectedRow={selectedRowForModal || {}}
      />}
      {/* <DocumentRegistrationModal open={documentRegistrationModalOpen} onClose={() => setDocumentRegistrationModalOpen(false)} data={selectedRowForModal || {}} /> */}
    </div>
  );
};